import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Capacitor } from '@capacitor/core';

const MIN_NATIVE_SPLASH_MS = 1500; // Minimum time the native splash must be visible to ensure smooth transition to dashboard

export function GlobalSplashScreen() {
    const { isPageReady, hasInitialized } = useAuth();
    const mountTimeRef = useRef(Date.now());

    // The splash screen stays visible until the app has initialized auth AND
    // the target page has signaled it is ready for its first paint.
    const isVisible = !hasInitialized || !isPageReady;

    // Hide the NATIVE splash screen — but enforce a minimum display duration
    // to prevent it from flickering away before the user can see it.
    // On fast devices/warm starts, auth can resolve in <100ms; without this guard,
    // the native splash would be hidden before the user ever sees it.
    useEffect(() => {
        if (!isVisible && Capacitor.isNativePlatform()) {
            const elapsed = Date.now() - mountTimeRef.current;
            const remaining = Math.max(0, MIN_NATIVE_SPLASH_MS - elapsed);

            const timer = setTimeout(() => {
                // Try to hide native splash screen if available
                try {
                    // @ts-ignore - Capacitor modules may not be available in web build
                    import('@capacitor/splash-screen').then(({ SplashScreen }) => {
                        if (SplashScreen && typeof SplashScreen.hide === 'function') {
                            // Return the hide() promise so its rejection ("Unable to hide
                            // Splash Screen" on warm start / already-hidden) is caught by the
                            // .catch() below instead of becoming an unhandled rejection.
                            return SplashScreen.hide({ fadeOutDuration: 400 });
                        }
                    }).catch(() => {
                        // Silently ignore — web-only fallback / benign native hide rejection
                    });
                } catch (e) {
                    // Silently ignore any errors
                }
            }, remaining);

            return () => clearTimeout(timer);
        }
    }, [isVisible]);

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ opacity: 1 }}
                    exit={{
                        opacity: 0,
                        transition: { duration: 0.8, ease: [0.4, 0, 0.2, 1] }
                    }}
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-br from-sky-100 via-slate-50 to-indigo-100"
                >
                    <motion.div
                        initial={{ opacity: 1, scale: 1 }}
                        exit={{
                            opacity: 0,
                            scale: 1.1,
                            transition: { duration: 0.6, ease: "easeOut" }
                        }}
                        className="flex flex-col items-center gap-6"
                    >
                        <img
                            src="/deskbay-logo-text.png"
                            alt="Deskbay"
                            className="w-32 md:w-40"
                        />
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
